import { memo, useEffect } from 'react';
import { Grid, Divider, useEventCallback } from '@mui/material';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { ForeignVM } from 'practice/view-models/pratice-actual-residence';
import { FormikErrors, FormikTouched } from 'formik';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityList, {
  MunicipalityAnprVM,
} from 'shell/components/shared/foreign-municipality-list';
import PmiTextField from 'shell/components/shared/text-field';
import { noop } from 'shell/utils/noop';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Municipality } from 'shell/models/municipality';
import { AxiosResponse } from 'axios';
import { HttpResponse } from 'shell/models/http-response';

interface FormProps {
  values?: ForeignVM;
  onChange: (key: keyof ForeignVM, value: ForeignVM[keyof ForeignVM]) => void;
  onChanges: (values: ForeignVM) => void;
  errors?: FormikErrors<ForeignVM>;
  touched?: FormikTouched<ForeignVM>;
  disabled?: boolean;
  ForPracticeTemporary?: boolean;
}

const ForeignFormComponent = ({
  values,
  onChange,
  onChanges,
  errors,
  touched,
  disabled,
}: FormProps): React.ReactElement => {
  const onChangeForeignState = useEventCallback((state: ForeignStateVM | null | undefined) => {
    onChanges({ foreignState: state, municipality: null });
  });

  const onChangeForeignMunicipality = useEventCallback(
    (municipality: MunicipalityAnprVM | null | undefined) => {
      onChange('municipality', municipality);
    }
  );

  const getConsulateTerritory = useHttpClient(`${__MUNICIPALITY_API_URL__}/geo/comune`, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  useEffect(() => {
    if (!values?.municipality?.id) {
      return;
    }

    (
      getConsulateTerritory({ url: `/${values?.municipality?.id || ''}` }) as ResponsePromise<void>
    ).then((response) => {
      const castedResponse = response as unknown as AxiosResponse<HttpResponse<Municipality>>;

      const castedResponseData = castedResponse.data.payload as Municipality;

      onChanges({
        ...(values || {}),
        consulateCode: {
          id: castedResponseData.comuneTerritorioConsolato?.territorioConsolato?.key
            ?.codiceConsolato
            ? Number.parseInt(
                castedResponseData.comuneTerritorioConsolato?.territorioConsolato?.key
                  .codiceConsolato,
                10
              )
            : '',
          value:
            castedResponseData.comuneTerritorioConsolato?.territorioConsolato?.consolato?.nome ||
            '',
        },
        territory: {
          id: castedResponseData.comuneTerritorioConsolato?.territorioConsolato?.key
            ?.codiceTerritorio
            ? Number.parseInt(
                castedResponseData.comuneTerritorioConsolato?.territorioConsolato?.key
                  .codiceTerritorio,
                10
              )
            : '',
          value:
            castedResponseData.comuneTerritorioConsolato?.territorioConsolato?.territorio
              ?.esonimo || '',
        },
      });
    });
  }, [values?.municipality]);

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <PmiTitleDivider>Località estera</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} container spacing={2}>
        <Grid item xs={12} md={6}>
          <ForeignStateList
            label="Stato estero"
            value={values?.foreignState || null}
            onChange={onChangeForeignState}
            id="foreignState"
            disabled={disabled}
            error={touched?.foreignState && Boolean(errors?.foreignState)}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <MunicipalityList
          value={values?.municipality}
          disabled={!values?.foreignState?.istatCode || disabled}
          istatCodeState={values?.foreignState?.istatCode}
          onChange={onChangeForeignMunicipality}
          label="Comune estero"
          id="foreignMunicipality"
          error={touched?.municipality && Boolean(errors?.municipality)}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Provincia/Contea"
          aria-label="Indirizzo"
          id="indirizzo"
          value={values?.municipality?.province || ''}
          onChange={noop}
          disabled
          fullWidth
        />
      </Grid>
      <Grid item xs={12} container spacing={2}>
        <Grid item xs={12} md={4}>
          <PmiTextField
            label="CAP"
            aria-label="CAP"
            id="cap"
            disabled={disabled}
            value={values?.cap || ''}
            onChange={(event) => onChange('cap', event.target.value)}
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={8}>
        <PmiTextField
          label="Indirizzo"
          aria-label="Indirizzo"
          id="address"
          disabled={disabled}
          value={values?.address || ''}
          onChange={(event) => onChange('address', event.target.value)}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Nr. civico"
          aria-label="numero civico"
          id="civicNumber"
          value={values?.civicNumber || ''}
          onChange={(event) => onChange('civicNumber', event.target.value)}
          fullWidth
          type="number"
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          aria-label="Consolato"
          label="Consolato"
          id="consulateCode"
          name="consulateCode"
          fullWidth
          type="text"
          variant="outlined"
          disabled
          value={values?.consulateCode?.value || ''}
          onChange={noop}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          aria-label="Territorio"
          label="Territorio"
          id="territory"
          name="territory"
          fullWidth
          type="text"
          variant="outlined"
          disabled
          value={values?.territory?.value || ''}
          onChange={noop}
        />
      </Grid>
    </Grid>
  );
};

ForeignFormComponent.displayName = 'Accordion New Residence Form - Foreign Form';

const ForeignForm = memo(ForeignFormComponent);

export default ForeignForm;
